import { useEffect, useState } from "react";
import IProcessed_Report from "../../../../models/IProcessed_Report";
import IOwTableCols from "../../../../models/IOwTableCols";
import IOwTableConfig from "../../../../models/IOwTableConfig";
import IOnGetInventoryStockLevelsReportDTO from "../../../../models/DTO/IOnGetInventoryStockLevelsReportDTO";
import OwButton from "../../../shared/OwButton/OwButton";
import OwLabel from "../../../shared/OwLabel/OwLabel";
import { OwFnTable } from "../../../shared/OwFnTable/OwFnTable";
import UserService from "../../../../services/UserService";
import ExcelJS from "exceljs/dist/exceljs.min.js";
import { FormattedNumber } from "react-intl";
import { useAppContextNonNull } from "@contexts/AppContext";

type Props = {
    processedReport: IProcessed_Report;
};

export const InventoryStockLevelsReport = (props: Props) => {
    const { supplierModel } = useAppContextNonNull();

    const minimumFractionDigits = supplierModel.qty_precision || 0;
    const maximumFractionDigits = supplierModel.qty_precision || 2;

    const getTableCols = (): IOwTableCols[] => {
        return [
            {
                title: "name",
                valueProp: "name",
                className: "w-20",
            },
            {
                title: "sku",
                valueProp: "sku",
                className: "w-20",
            },
            {
                title: "total",
                valueProp: "physical_qty_on_hand",
                renderFnEmitsRow: true,
                renderFn: (item) => (
                    <FormattedNumber
                        value={item.physical_qty_on_hand || item.qty_on_hand + item.reserved_qty_on_hand}
                        minimumFractionDigits={minimumFractionDigits}
                        maximumFractionDigits={maximumFractionDigits}
                    />
                ),
                className: "w-20",
            },
            {
                title: "available",
                valueProp: "qty_on_hand",
                renderFnEmitsRow: true,
                renderFn: (item) => (
                    <FormattedNumber
                        value={item.qty_on_hand}
                        minimumFractionDigits={minimumFractionDigits}
                        maximumFractionDigits={maximumFractionDigits}
                    />
                ),
                className: "w-20",
            },
            {
                title: "reserved",
                valueProp: "reserved_qty_on_hand",
                renderFnEmitsRow: true,
                renderFn: (item) => (
                    <FormattedNumber
                        value={item.reserved_qty_on_hand}
                        minimumFractionDigits={minimumFractionDigits}
                        maximumFractionDigits={maximumFractionDigits}
                    />
                ),
                className: "w-20",
            },
            {
                title: "incoming",
                valueProp: "qty_incoming",
                renderFnEmitsRow: true,
                renderFn: (item) => (
                    <FormattedNumber
                        value={item.qty_incoming}
                        minimumFractionDigits={minimumFractionDigits}
                        maximumFractionDigits={maximumFractionDigits}
                    />
                ),
                className: "w-20",
            },
        ];
    };

    const tableConfig: IOwTableConfig = {
        tableHover: true,
        stickyHeader: true,
        stickyHeaderMaxHeight: "calc(100vh - 250px)",
    };

    const [reportData, setReportData] = useState<IOnGetInventoryStockLevelsReportDTO>();
    const [tableCols, setTableCols] = useState<IOwTableCols[]>(getTableCols());

    const onGetInventoryStockLevelsReport = (data: IOnGetInventoryStockLevelsReportDTO) => {
        console.log("onGetInventoryStockLevelsReport: ", data);
        setReportData(data);
    };

    useEffect(() => {
        onGetInventoryStockLevelsReport(props.processedReport.report_data);
    }, []);

    const downloadExcel = () => {
        if (reportData) {
            const workbook = new ExcelJS.Workbook();
            const ws = workbook.addWorksheet(`Inventory Stock Levels`);

            const salesPeopleWs: { salesperson: string; ws: ExcelJS.Worksheet }[] = [];

            const columns: (
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
                | { header: string; key: string }
            )[] = [
                { header: "Name", key: "name" },
                { header: "SKU", key: "sku" },
                { header: "Total", key: "totalQtyOnHand" },
                { header: "Available", key: "qtyOnHand" },
                { header: "Reserved ", key: "reservedQtyOnHand" },
                { header: "Incoming", key: "qtyIncoming" },
            ];

            ws.columns = columns;

            ws.getCell("A1").font = {
                bold: true,
            };

            ws.getCell("B1").font = {
                bold: true,
            };

            ws.getCell("C1").font = {
                bold: true,
            };

            ws.getCell("D1").font = {
                bold: true,
            };

            ws.getCell("E1").font = {
                bold: true,
            };

            for (const item of reportData.data) {
                const name = item.name;
                const sku = item.sku;
                const totalQtyOnHand = item.physical_qty_on_hand;
                const qtyOnHand = item.qty_on_hand;
                const reservedQtyOnHand = item.reserved_qty_on_hand;
                const qtyIncoming = item.qty_incoming;

                ws.addRow({ name, sku, totalQtyOnHand, qtyOnHand, reservedQtyOnHand, qtyIncoming });
            }

            // const periodStart = moment(reportData.periodStart).format("YYYY-MM-DD");
            // const periodEnd = moment(reportData.periodEnd).format("YYYY-MM-DD");

            workbook.xlsx.writeBuffer().then((data) => {
                const blob = new Blob([data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                const url = window.URL.createObjectURL(blob);
                const anchor = document.createElement("a");
                anchor.href = url;
                anchor.download = `orderwerks-inventory-stock-levels-${UserService.parseLastUpdated(
                    props.processedReport.created_at
                )}.xlsx`;
                anchor.click();
                window.URL.revokeObjectURL(url);
            });
        }
    };

    return (
        <div className={"InventoryStockLevelsReport"}>
            {reportData && reportData.data.length > 0 ? <OwButton title={"export_to_excel"} fn={downloadExcel} /> : null}
            <div>
                <OwLabel title={"total_records"} postText={":"} isBold={true} className={"me-2 mb-2 mt-2"} />
                {reportData?.data.length}
                <div>
                    <OwLabel title={"created_at"} className={"me-2"} postText={":"} />
                    {UserService.parseLastUpdated(props.processedReport.created_at)}
                </div>
            </div>

            <div className={"mb-5"}>
                <OwFnTable
                    columns={tableCols}
                    dataSet={reportData?.data || []}
                    dataFetch={false}
                    initialData={false}
                    tableConfig={tableConfig}
                />
            </div>
        </div>
    );
};
